import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FiUpload, FiInfo } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { uploadAndParseBatch, clearParsedData } from './billParsingSlice';
import { LoadingState } from '../../utilities/constants';
import { parseUserInfo } from '../../utilities/common';
import ParsedDataView from './ParsedDataView';

const UploadBillTab = ({ onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orgId } = parseUserInfo(useSelector((state) => state.users.user));
  const { batchParseStatus, batchParseResults } = useSelector((state) => state.billParsing);
  const [files, setFiles] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  const schema = yup.object().shape({
    selectedFile: yup.mixed().required(t('Please select at least one PDF file'))
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange'
  });

  useEffect(() => {
    if (!batchParseResults) {
      reset();
      setFiles([]);
      setFileInputKey(Date.now());
    }
  }, [batchParseResults, reset]);

  const isParsing = batchParseStatus === LoadingState.pending;
  const isLoading = isParsing;
  const hasResults = batchParseResults;

  const onSubmit = async () => {
    await dispatch(uploadAndParseBatch({ orgId, files }));
  };

  const handleCancel = () => {
    dispatch(clearParsedData());
    reset();
    setFiles([]);
    setFileInputKey(Date.now());
  };

  return (
    <div>
      <Card className="mb-3">
        <Card.Body>
          <h5 className="mb-3">{t('Upload PDF Bill')}</h5>

          <Alert variant="primary" className="mb-3 d-flex align-items-center">
            <FiInfo className="mr-2" size={18} style={{ minWidth: '18px', minHeight: '18px', flexShrink: 0 }} />
            <span>
              {t(
                'The user interface allows a maximum of twelve invoices per upload. If you wish to upload more than twelve invoices at once, please'
              )}{' '}
              <a href="https://support.simblegroup.com/hc/en-us" target="_blank" rel="noopener noreferrer">
                {t('contact Simble staff')}
              </a>
              {t(', who can provide an alternative bulk upload method.')}
            </span>
          </Alert>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <Form.Group>
              <Controller
                name="selectedFile"
                control={control}
                render={({ field }) => {
                  const handleSelectFile = (e) => {
                    const fileList = Array.from(e.target.files);
                    if (fileList.length > 12) {
                      toast.error(t('Maximum 12 invoices allowed per upload'));
                      field.onChange(null);
                      setFiles([]);
                      e.target.value = '';
                      return;
                    }
                    if (fileList.length > 0) {
                      field.onChange(fileList[0]);
                      setFiles(fileList);
                    }
                  };
                  return (
                    <>
                      <Form.File
                        key={fileInputKey}
                        id="billFileUploadControl"
                        label={t('Select PDF File')}
                        accept=".pdf,application/pdf"
                        className="form-content"
                        onChange={handleSelectFile}
                        isInvalid={!!errors.selectedFile}
                        disabled={isLoading || hasResults}
                        multiple
                      />
                      {errors.selectedFile && <span className="required-span">{errors.selectedFile.message}</span>}
                    </>
                  );
                }}
              />
            </Form.Group>

            {isLoading && (
              <Alert variant="info" className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="mr-2" />
                <span>{t('Processing...')}</span>
              </Alert>
            )}

            <Button
              type="submit"
              variant="secondary"
              disabled={!isValid || isLoading || hasResults}
              className="d-flex align-items-center"
            >
              <FiUpload className="mr-2" />
              {t('Upload & Parse')}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {batchParseResults && (
        <ParsedDataView parsedData={batchParseResults} onCancel={handleCancel} onSuccess={onSuccess} />
      )}
    </div>
  );
};

export default UploadBillTab;
